import {FC, memo, ReactNode, useMemo, useRef} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {Box, Center, Textarea} from '@chakra-ui/react';
import {api, useT} from '@kcast-fe/core';
import {EllipseButton} from 'ui-kit';

interface IProps {
  isDisabled: boolean;
  isSendingDisabled: boolean;
  tryToSendMsg: (msg: string) => void;
  documentSlot: ReactNode;
  voiceSlot: ReactNode;
}

const MessageFormFC: FC<IProps> = ({
  isDisabled,
  tryToSendMsg,
  isSendingDisabled,
  documentSlot,
  voiceSlot
}) => {
  const {data: settings} = api.settings.useSettingsQuery();

  const pressedKeys = useRef(new Set<string>());

  const {t} = useT();

  const {control, watch, reset} = useForm<{msg: string}>({defaultValues: {msg: ''}});
  const [currentMgs] = watch(['msg']);

  const handleSendMsg = () => {
    if (!isSendingDisabled) {
      tryToSendMsg(currentMgs);
      reset({msg: ''});
    }
  };

  const placeholder = useMemo(() => {
    return settings ? t('placeholders.chat', {name: settings?.assistant?.name}) : '';
  }, [settings, t]);

  return (
    <Controller
      name="msg"
      control={control}
      rules={{required: true}}
      render={({field: {onChange, value}}) => (
        <Box
          zIndex={1}
          position={'relative'}
          className={'message-form'}
          data-testid={'MessageForm'}
        >
          <Box
            padding={'4px'}
            borderRadius={{base: '32px'}}
            transition={'background var(--tr-100-ei)'}
            _focusWithin={{borderColor: '{colors.bgMain}'}}
          >
            <Textarea
              rows={1}
              w={'100%'}
              size={'lg'}
              bg={'white'}
              value={value}
              outline={'0'}
              resize={'none'}
              display={'block'}
              autoresize
              maxH={'154px'}
              disabled={isDisabled}
              p={{base: '16px 54px 16px 48px'}}
              fontSize={{base: '16px'}}
              color={'{colors.textPrimary}'}
              borderRadius={{base: '30px'}}
              border={'thin solid'}
              borderColor={'{colors.bgMain}'}
              transition={'all var(--tr-100-ei)'}
              placeholder={placeholder}
              _focus={{borderColor: '{colors.bgMain}'}}
              _hover={{borderColor: '{colors.bgMain}'}}
              onKeyDown={(e) => {
                pressedKeys.current.add(e.key);
                if (!pressedKeys.current.has('Shift') && e.key === 'Enter' && currentMgs) {
                  e.preventDefault();
                  handleSendMsg();
                }
              }}
              onKeyUp={(e) => {
                pressedKeys.current.delete(e.key);
              }}
              onChange={onChange}
              data-testid={'message-input'}
            />

            <Center
              h={'100%'}
              w={'50px'}
              position={'absolute'}
              color={'{colors.textSecondary}'}
              left={'4px'}
              top={0}
            >
              {documentSlot}
            </Center>

            <Center
              h={'100%'}
              w={'54px'}
              position={'absolute'}
              color={'{colors.textSecondary}'}
              right={'4px'}
              top={0}
            >
              {currentMgs ? (
                <EllipseButton
                  icon={'Enter'}
                  size={'normal'}
                  iconSize={'s28'}
                  variant={'transparent'}
                  isDisabled={isDisabled || isSendingDisabled}
                  onClick={handleSendMsg}
                />
              ) : (
                <Box pb={'2px'}>{voiceSlot}</Box>
              )}
            </Center>
          </Box>
        </Box>
      )}
    />
  );
};

export const MessageForm = memo(MessageFormFC);
